"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_router_dom_1 = require("react-router-dom");
const common_styled_components_1 = require("../../common-styled-components");
const useFetch_1 = __importDefault(require("../../../hooks/useFetch"));
const compliance_pool_types_1 = require("../compliance-pool-types");
const apiUtils_1 = require("../../../util/apiUtils");
const useMetadata_1 = require("../../../hooks/useMetadata");
const MultiNumberKPI_1 = __importDefault(require("../common/MultiNumberKPI"));
const compliance_1 = require("../../../util/compliance/compliance");
const LineChart_1 = __importDefault(require("../LineChart"));
const theme_1 = require("../../../theme");
const util_1 = require("../util");
const log_1 = require("../../../util/log");
const ShipTable_1 = require("../common/tables/ShipTable");
const TransactionTable_1 = require("../common/tables/TransactionTable");
const BookingTable_1 = require("../common/tables/BookingTable");
const isNonEmptyObject_1 = require("../../../util/isNonEmptyObject");
const YearContext_1 = require("../../../contexts/YearContext");
const react_1 = require("react");
const NavigationContext_1 = require("../../../contexts/NavigationContext");
exports.default = () => {
    const { selectedYear } = (0, YearContext_1.useYear)();
    const { poolId } = (0, react_router_dom_1.useParams)();
    const [pools, _setUrl, _setBody, _refresh] = (0, useFetch_1.default)("admin/pools.json", undefined, true);
    const [usageAnalysis, _setUsageUrl, _setUsageBody, _refreshUsage] = (0, useFetch_1.default)("pool/pool_fuel_usage.json", undefined, true);
    const [transactions, _setTransUrl, _setTransPostBody, refreshTransactions] = (0, useFetch_1.default)("admin/transactions.json", undefined, true);
    const [bookings, _setBookingsUrl, _setBookingsBody, refreshBookings] = (0, useFetch_1.default)("admin/pool_bookings.json", undefined, true);
    const [expectedInventory, _setExpectedInventoryUrl, _setExpectedInventoryBody, refreshExpectedInventory,] = (0, useFetch_1.default)("admin/pool_compliance_inventory_expected.json", undefined, true);
    const [validatedInventory, _setValidatedInventoryUrl, _setValidatedInventoryBody, refreshValidatedInventory,] = (0, useFetch_1.default)("admin/pool_compliance_inventory_validated.json", undefined, true);
    (0, react_1.useEffect)(() => {
        const body = JSON.stringify({
            pool_id: Number(poolId),
            year: selectedYear,
        });
        _setBody(body);
        _setUsageBody(body);
        _setTransPostBody(body);
        _setBookingsBody(body);
        _setExpectedInventoryBody(body);
        _setValidatedInventoryBody(body);
    }, [poolId, selectedYear]);
    const [metadata] = (0, useMetadata_1.useMetadata)();
    const poolData = pools.status === apiUtils_1.LoaderStatus.Success ? pools.data[0] : null;
    const usageData = usageAnalysis.status === apiUtils_1.LoaderStatus.Success
        ? usageAnalysis.data
        : undefined;
    const analysis = usageData?.pool.analysis;
    const transactionData = transactions.status === apiUtils_1.LoaderStatus.Success ? transactions.data : null;
    const bookingData = bookings.status === apiUtils_1.LoaderStatus.Success ? bookings.data : null;
    const expectedInventoryData = expectedInventory.status === apiUtils_1.LoaderStatus.Success
        ? expectedInventory.data
        : undefined;
    const validatedInventoryData = validatedInventory.status === apiUtils_1.LoaderStatus.Success
        ? validatedInventory.data
        : undefined;
    const poiData = transactionData?.map((t) => (0, util_1.getTransactionPOI)(t, "neutral"));
    const forecastsAvailable = (0, compliance_pool_types_1.isForecast)(usageData?.pool.forecast) &&
        usageData?.pool.forecast.total !== undefined;
    const forecastFuels = forecastsAvailable
        ? (0, util_1.combineFuels)(usageData.pool.forecast.forecast_fuels_total)
        : undefined;
    const forecastDate = forecastsAvailable
        ? new Date(usageData.pool.forecast.end)
        : undefined;
    (0, log_1.log)([
        {
            label: "Metadata",
            content: metadata,
        },
        {
            label: "Pool data",
            content: poolData ?? "loading",
        },
        {
            label: "Usage analysis",
            content: usageData ?? "loading",
        },
    ]);
    const carbonLimitYear = selectedYear;
    const getInventoryColor = (value) => value === undefined
        ? theme_1.theme.colors.blackText
        : compliance_1.complianceOptions[(0, compliance_1.getCompliance)(value)].color;
    const currentStatusItems = [
        {
            color: (analysis &&
                compliance_1.complianceOptions[(0, compliance_1.getCompliance)(analysis?.intensities.total.adjusted_emission_balance_ton)].color) ??
                theme_1.theme.colors.blackText,
            title: "Carbon intensity",
            subtitle: "Report-based",
            unit: "gCO₂e/MJ",
            value: analysis?.intensities.total.adjusted_intensity_gCO2e_per_MJ,
            digits: 2,
        },
        {
            color: (analysis &&
                compliance_1.complianceOptions[(0, compliance_1.getCompliance)(analysis?.intensities.total.emission_balance_ton)].color) ??
                theme_1.theme.colors.blackText,
            title: "Emission balance",
            subtitle: "Report-based",
            unit: "tCO₂e",
            value: analysis?.intensities.total.adjusted_emission_balance_ton,
            digits: 0,
            showPositiveSign: true,
        },
    ];
    const inventoryItems = [
        {
            color: theme_1.theme.colors.blackText,
            value: expectedInventoryData?.expected_bookable_inventory_tco2e,
            title: "Expected inventory",
            subtitle: "EOY estimate",
            unit: "tCO₂e",
            digits: 2,
            showPositiveSign: true,
        },
        {
            color: theme_1.theme.colors.blackText,
            value: expectedInventoryData?.total_booked_tco2e,
            title: "Booked",
            subtitle: "Booked total",
            unit: "tCO₂e",
            digits: 2,
        },
        {
            color: getInventoryColor(expectedInventoryData?.available_after_bookings_tco2e),
            value: expectedInventoryData?.available_after_bookings_tco2e,
            title: "Available after bookings",
            subtitle: "EOY estimate",
            unit: "tCO₂e",
            digits: 2,
            showPositiveSign: true,
        },
    ];
    const validatedInventoryItems = [
        {
            color: theme_1.theme.colors.blackText,
            value: validatedInventoryData?.validated_balance_tco2e,
            title: "Validated balance",
            subtitle: "Validated inventory",
            unit: "tCO₂e",
            digits: 1,
            showPositiveSign: true,
        },
        {
            color: theme_1.theme.colors.blackText,
            value: validatedInventoryData?.total_transaction_quantity_tco2e,
            title: "Transactions",
            subtitle: "Validated inventory",
            unit: "tCO₂e",
            digits: 1,
        },
        {
            color: theme_1.theme.colors.blackText,
            value: validatedInventoryData?.remaining_after_transactions_tco2e,
            title: "Remaining after transactions",
            subtitle: "Validated inventory",
            unit: "tCO₂e",
            digits: 1,
            showPositiveSign: true,
        },
    ];
    if (poolData && analysis && metadata) {
        return ((0, jsx_runtime_1.jsxs)(common_styled_components_1.PageContent, { align: "left", children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.ContentGrid, { children: (0, jsx_runtime_1.jsxs)(common_styled_components_1.GridCard, { "$cols": 3, children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.TitleWithExtraContent, { titleLevel: "Section", extraContent: (0, jsx_runtime_1.jsx)(NavigationContext_1.LinkWithHistory, { to: `eoy-report`, style: {
                                        marginTop: theme_1.theme.spacing(2),
                                        color: theme_1.theme.colors.bluelink,
                                        fontSize: theme_1.theme.fontSizeNumber(-1),
                                    }, children: "End-of-year Report" }), children: poolData.name }), (0, jsx_runtime_1.jsxs)(common_styled_components_1.PTight, { children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.Bold, { children: "Year: " }), poolData.year] }), (0, jsx_runtime_1.jsxs)(common_styled_components_1.PTight, { children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.Bold, { children: "Ships: " }), poolData.ships.length] })] }) }), (0, jsx_runtime_1.jsxs)(common_styled_components_1.ContentGrid, { children: [(0, jsx_runtime_1.jsxs)(common_styled_components_1.GridCard, { children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.SmallSubtitle, { style: { marginBottom: theme_1.theme.spacing(0) }, children: "Current balance" }), (0, jsx_runtime_1.jsx)(common_styled_components_1.PSmall, { children: "Report-based" }), (0, jsx_runtime_1.jsx)(MultiNumberKPI_1.default, { items: currentStatusItems })] }), (0, jsx_runtime_1.jsxs)(common_styled_components_1.GridCard, { children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.SmallSubtitle, { style: { marginBottom: theme_1.theme.spacing(0) }, children: "Validated inventory" }), (0, jsx_runtime_1.jsx)(common_styled_components_1.PSmall, { children: "Periodical Statements" }), (0, jsx_runtime_1.jsx)(MultiNumberKPI_1.default, { items: validatedInventoryItems })] }), (0, jsx_runtime_1.jsxs)(common_styled_components_1.GridCard, { children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.SmallSubtitle, { style: {
                                        marginBottom: theme_1.theme.spacing(0),
                                    }, children: "Bookings" }), (0, jsx_runtime_1.jsx)(common_styled_components_1.PSmall, { children: "Balance plans/forecasts" }), (0, jsx_runtime_1.jsx)(MultiNumberKPI_1.default, { items: inventoryItems })] })] }), (0, jsx_runtime_1.jsxs)(common_styled_components_1.ContentGrid, { "$cols": 2, children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.GridCard, { children: (0, jsx_runtime_1.jsx)(LineChart_1.default, { title: "Pool carbon intensity", unit: "gCO\u2082e/MJ", poiData: poiData, data: [
                                    {
                                        name: "Intensity",
                                        color: theme_1.theme.colors.blackText,
                                        data: analysis.intensities.cumulative?.map((d) => ({
                                            date: new Date(d.date),
                                            value: d.intensity.adjusted_intensity_gCO2e_per_MJ,
                                        })) ?? [],
                                        prediction: forecastsAvailable && forecastDate
                                            ? {
                                                date: forecastDate,
                                                value: usageData.pool.forecast.total
                                                    .adjusted_intensity_gCO2e_per_MJ,
                                            }
                                            : undefined,
                                    },
                                ], limits: [
                                    {
                                        label: `${carbonLimitYear} limit`,
                                        value: metadata.intensity[carbonLimitYear],
                                        color: theme_1.theme.colors.red,
                                    },
                                ] }) }), (0, jsx_runtime_1.jsx)(common_styled_components_1.GridCard, { children: (0, jsx_runtime_1.jsx)(LineChart_1.default, { title: "Carbon unit balance", unit: "-tCO\u2082e", poiData: poiData, data: [
                                    {
                                        name: "Carbon unit balance",
                                        color: theme_1.theme.colors.blackText,
                                        data: analysis.intensities.cumulative?.map((d) => ({
                                            date: new Date(d.date),
                                            value: d.intensity.emission_balance_ton,
                                        })) ?? [],
                                        prediction: forecastsAvailable && forecastDate
                                            ? {
                                                date: forecastDate,
                                                value: usageData.pool.forecast.total
                                                    .adjusted_emission_balance_ton,
                                            }
                                            : undefined,
                                    },
                                ] }) }), (0, jsx_runtime_1.jsx)(common_styled_components_1.GridCard, { children: (0, jsx_runtime_1.jsx)(LineChart_1.default, { title: "Cumulative emissions", unit: "tCO\u2082e", poiData: poiData, data: [
                                    {
                                        name: "Emissions",
                                        color: theme_1.theme.colors.blackText,
                                        data: analysis.intensities.cumulative?.map((d) => ({
                                            date: new Date(d.date),
                                            value: d.intensity.co2_emission_ton,
                                        })) ?? [],
                                        prediction: forecastsAvailable && forecastDate
                                            ? {
                                                date: forecastDate,
                                                value: usageData.pool.forecast.total
                                                    .adjusted_co2_emission_ton,
                                            }
                                            : undefined,
                                    },
                                    {
                                        name: "Emission limit",
                                        color: theme_1.theme.colors.red,
                                        dashed: true,
                                        data: analysis.intensities.cumulative?.map((d) => ({
                                            date: new Date(d.date),
                                            value: d.intensity.emission_limit_ton,
                                        })) ?? [],
                                        prediction: forecastsAvailable && forecastDate
                                            ? {
                                                date: forecastDate,
                                                value: usageData.pool.forecast.total.emission_limit_ton,
                                            }
                                            : undefined,
                                    },
                                ] }) }), analysis.fuel_spent.cumulative_fuel_timeline &&
                            (0, isNonEmptyObject_1.isNonEmptyObject)(analysis.fuel_spent.cumulative_fuel_timeline) && ((0, jsx_runtime_1.jsx)(common_styled_components_1.GridCard, { children: (0, jsx_runtime_1.jsx)(LineChart_1.default, { title: "Cumulative fuel use", unit: "t", data: Object.keys(analysis.fuel_spent.cumulative_fuel_timeline).map((fuelKey, i) => {
                                    const fuelData = analysis.fuel_spent.cumulative_fuel_timeline[fuelKey];
                                    return {
                                        name: metadata.fuels[fuelData.fuel]?.description ??
                                            "Name not found",
                                        color: (0, util_1.getFuelColor)(fuelData.fuel),
                                        data: fuelData.timeline.map((d) => ({
                                            date: new Date(d.date),
                                            value: d.amount,
                                        })),
                                        prediction: undefined,
                                        // forecastFuels && forecastDate
                                        //   ? {
                                        //       date: forecastDate,
                                        //       value: forecastFuels[fuelKey],
                                        //     }
                                        //   : undefined,
                                    };
                                }) }) }))] }), (0, jsx_runtime_1.jsx)(common_styled_components_1.ContentGrid, { "$cols": 1, children: (0, jsx_runtime_1.jsxs)(common_styled_components_1.GridCard, { children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.SmallSubtitle, { style: { marginBottom: theme_1.theme.spacing(2) }, children: "Ships" }), (0, jsx_runtime_1.jsx)(ShipTable_1.ShipTable, { ships: poolData.ships, metadata: metadata, year: carbonLimitYear, showCompany: true, isInAdminTools: true })] }) }), (0, jsx_runtime_1.jsx)(common_styled_components_1.ContentGrid, { "$cols": 1, children: (0, jsx_runtime_1.jsxs)(common_styled_components_1.GridCard, { children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.SmallSubtitle, { style: { marginBottom: theme_1.theme.spacing(2) }, children: "Bookings" }), (0, jsx_runtime_1.jsx)(BookingTable_1.BookingTable, { bookings: bookingData ?? [], pool: poolData, refresh: () => {
                                    refreshBookings();
                                    refreshExpectedInventory();
                                    refreshValidatedInventory();
                                }, isInAdminTools: true })] }) }), transactionData && transactionData.length >= 0 && ((0, jsx_runtime_1.jsx)(common_styled_components_1.ContentGrid, { "$cols": 1, children: (0, jsx_runtime_1.jsxs)(common_styled_components_1.GridCard, { children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.SmallSubtitle, { style: { marginBottom: theme_1.theme.spacing(2) }, children: "Compliance transactions" }), (0, jsx_runtime_1.jsx)(TransactionTable_1.TransactionTable, { transactions: transactionData, complianceYear: carbonLimitYear, poolId: poolData.id, refresh: refreshTransactions, isInAdminTools: true })] }) }))] }));
    }
    return (0, jsx_runtime_1.jsx)(common_styled_components_1.PageContent, { children: "Loading pool\u2026" });
};
